import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, merge, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { PatientModelDto } from '@shared/models/rx-models/interfaces/patient-model-dto';
import { ShellQuery } from '@shared/store/shell/shell-query';
import type { Maybe } from '@/types/globals';
import { PatientQuery } from '@modules/patient/state/patient-query';
import { PatientStore } from '@modules/patient/state/patient-store';
import { PatientAppInitService } from '@modules/patient/services/patient-app-init.service';
import { PatientAppIframeCommunicationService } from '@modules/patient/services/patient-app-iframe-communication.service';
import { PatientAppUrlBuilderService } from '@modules/patient/services/patient-app-url-builder.service';
import { PatientDisplayMode } from '@modules/patient/models/patient-display-mode.enum';
import { PatientDisplayModeService } from '@modules/patient/services/patient-display-mode.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PatientCardCommunicationService } from '@modules/patient-card/services/patient-card-communication.service';

/**
 * Facade for rx-intuitive-patient-header.
 *
 * Responsibilities:
 * - Ensure patient data is loaded from Rx into store (for collapsed header display)
 * - Expose patient state for collapsed header info cells
 * - Manage expand/collapse state with snapshot-restore logic
 * - Gate edit button via read-only state
 * - Expose display mode for the expanded area (PatientCard / PatientApp / UnifiedPatients)
 */
@Injectable()
export class RxIntuitivePatientHeaderFacade {
	private readonly patientExpandedSubject = new BehaviorSubject<boolean>(false);

	private openedPatientSnapshot: Maybe<PatientModelDto> = null;
	private isInitialUpdateAllowed = false;

	readonly patient$: Observable<Maybe<PatientModelDto>> = this.patientQuery.patient$;
	readonly isPatientCleared$: Observable<boolean> = this.patientQuery.isPatientCleared$;
	readonly dateFormat$: Observable<string> = this.shellQuery.dateFormat$;
	readonly patientExpanded$: Observable<boolean> = this.patientExpandedSubject.asObservable();
	readonly isPatientSectionReadOnly$: Observable<boolean> = this.shellQuery.getReadOnlyState('patient');
	readonly patientDisplayMode$: Observable<PatientDisplayMode> = this.patientDisplayModeService.displayMode$;

	/** Emits true when the patient is updated from the edit iframe (e.g. user clicks Update/Confirm). */
	readonly updateConfirmed$: Observable<boolean> = merge(
		this.patientAppIframeCommunicationService.patientUpdatedEvent$,
		this.patientCardCommunicationService.patientSaved$.pipe(map(event => event.data.success))
	);

	/** Tracks whether a patient was selected in the iframe, enabling the initial confirm action. */
	readonly patientSelectedEvent$: Observable<boolean> = this.patientAppIframeCommunicationService.patientSelectedEvent$.pipe(
		tap(selected => (this.isInitialUpdateAllowed = selected))
	);

	/** Initializes patient app streams and handles popup commands from the edit iframe. */
	readonly initializePatientAppStreams$: Observable<unknown> = merge(
		this.patientAppInitService.initializePatientAppStreams$,
		this.patientAppIframeCommunicationService.patientPopupCommandHandler$
	);

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly patientQuery: PatientQuery,
		private readonly patientStore: PatientStore,
		private readonly sanitizer: DomSanitizer,
		private readonly patientAppUrlBuilderService: PatientAppUrlBuilderService,
		private readonly patientAppInitService: PatientAppInitService,
		private readonly patientDisplayModeService: PatientDisplayModeService,
		private readonly patientAppIframeCommunicationService: PatientAppIframeCommunicationService,
		private readonly patientCardCommunicationService: PatientCardCommunicationService
	) {}

	get isInitialConfirmAllowed(): boolean {
		return this.isInitialUpdateAllowed;
	}

	get expandedPatientAppUrl$(): Observable<SafeResourceUrl | null> {
		return combineLatest([this.patientExpanded$, this.patient$]).pipe(
			map(([isExpanded]) => isExpanded),
			switchMap(isExpanded =>
				isExpanded
					? this.patientAppUrlBuilderService.getEditPatientAppUrl(true, this.isInitialUpdateAllowed).pipe(
							distinctUntilChanged(),
							map(url => this.sanitizer.bypassSecurityTrustResourceUrl(url))
						)
					: of(null)
			)
		);
	}

	toggleHeader(patient: Maybe<PatientModelDto>): void {
		const isExpanded = this.patientExpandedSubject.getValue();

		if (!isExpanded) {
			this.isInitialUpdateAllowed = false;
			this.openedPatientSnapshot = patient ? { ...patient } : null;
			this.patientExpandedSubject.next(true);

			return;
		}

		const currentStorePatient = this.patientQuery.patient;
		const openedPatientSnapshot = this.openedPatientSnapshot;
		const shouldRestorePatient = !!openedPatientSnapshot && !this.arePatientsEqual(currentStorePatient, openedPatientSnapshot);

		if (shouldRestorePatient) {
			// Keep patient store consistent if header is closed without confirming the changes.
			this.updatePatient(openedPatientSnapshot);
		}

		this.closeHeader();
	}

	closeHeader(): void {
		this.openedPatientSnapshot = null;
		this.patientExpandedSubject.next(false);
	}

	private updatePatient(patient: PatientModelDto): void {
		this.patientStore.updatePatient(patient, this.shellQuery.dateFormat, this.shellQuery.regulatoryDOBMask);
	}

	private arePatientsEqual(firstPatient: Maybe<PatientModelDto>, secondPatient: Maybe<PatientModelDto>): boolean {
		if (!firstPatient && !secondPatient) {
			return true;
		}

		if (!firstPatient || !secondPatient) {
			return false;
		}

		return firstPatient.Id === secondPatient.Id && firstPatient.UID === secondPatient.UID;
	}
}
